'use client';

import { useEffect, useEffectEvent } from 'react';

export type HitlApprovalShortcutLabels = {
  approve: string;
  reject: string;
};

const isApplePlatform = (): boolean =>
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform);

const EDITABLE_SELECTOR =
  'input, textarea, [contenteditable]:not([contenteditable="false"])';

const findEditableTarget = (
  target: EventTarget | null,
): HTMLElement | undefined => {
  if (!(target instanceof Element)) {
    return undefined;
  }

  const editable = target.closest(EDITABLE_SELECTOR);

  return editable instanceof HTMLElement ? editable : undefined;
};

const hasTypedText = (target: HTMLElement): boolean =>
  target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
    ? target.value.trim().length > 0
    : (target.textContent ?? '').trim().length > 0;

export function getHitlApprovalShortcutLabels(): HitlApprovalShortcutLabels {
  return isApplePlatform()
    ? { approve: '⌘⏎', reject: '⌘⌫' }
    : { approve: 'Ctrl+Enter', reject: 'Ctrl+Backspace' };
}

export function useHitlApprovalShortcuts({
  enabled,
  onApprove,
  onReject,
}: {
  enabled: boolean;
  onApprove: () => void;
  onReject: () => void;
}): void {
  const handleKeyDown = useEffectEvent((event: KeyboardEvent) => {
    const hasModifier = isApplePlatform() ? event.metaKey : event.ctrlKey;

    if (!hasModifier || event.altKey || event.shiftKey || event.repeat) {
      return;
    }

    const editableTarget = findEditableTarget(event.target);

    if (event.key === 'Enter') {
      if (editableTarget && hasTypedText(editableTarget)) {
        return;
      }

      event.preventDefault();
      onApprove();

      return;
    }

    if (event.key === 'Backspace' && !editableTarget) {
      event.preventDefault();
      onReject();
    }
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
}
